import { addMessage, updateMessageStatus, updateContactName } from "../redux/Slice/Inbox/InboxMessageSlice";
import { getCommentReplyPatch } from "./commentReplyState";
import { batch } from "react-redux";
import { addMessage as addTeamMemberMessage, updateMessageStatus as updateTeamMemberMessageStatus, updateInboxChat, fetchTeamMemberChats } from "../redux/Slice/Inbox/teamMemberChatSlice";
import { addMessageCard, removeContactFromInbox } from "../redux/Slice/Inbox/inboxRecentSlice";
import { addActiveMember, removeActiveMember, setselectedContactID } from "../redux/Slice/Inbox/inboxAllSlice";
import { calculateAndDispatchInboxCounts } from "../redux/Slice/Inbox/inboxRecentSlice";
import { setSelectedSettings } from "../redux/Slice/Inbox/inboxSlice";
import { removeContactFromAssignment, updateAssignmentCount, addContactToAssignment } from "../redux/Slice/Inbox/inboxSettingsSlice";
import { adjustAgentCount } from "../redux/Slice/AiAgents/aiAgentsSlice";
import { addScheduledMessage, removeScheduledMessage, updateScheduledMessage } from "../redux/Slice/Inbox/ScheduledMessageSlice";
import { taskChatSocketEnvelope, taskChatReadReceiptBatch, taskChatListSocketUpdate } from "../redux/Slice/Tasks/taskChatSlice";
import { applyTaskRealtimeUpdate, applyTaskRealtimeDelete } from "../redux/Slice/Tasks/tasksSlice";
import { notifyUser, playNotificationSound, showTaskChatBrowserNotification } from "./notificationUtils";
import {
  buildTaskLogNotificationBody,
  buildTaskLogNotificationTitle,
  rememberTaskDisplayName,
  resolveTaskDisplayName,
  resolveTaskDisplayNameForNotification,
} from "./taskAlertMessages";
import React from "react";
import toast from "react-hot-toast";
import { clearAccessToken, setEvicted } from "./tokenManager";
import { APP_TOAST_BACKGROUND, APP_TOAST_ERROR_COLOR, APP_TOAST_FONT_SIZE } from "../config/appToast";
import { findInboxContactInData, buildContactLikeForSocket, computeLastMessageThreadFlagsFromChat } from "./inboxThreadFlags";
import { invalidateCachedContact } from "../redux/Slice/Inbox/inboxInfoSlice";
import {
  socketState,
  DEBOUNCE_TIME,
  getDispatch,
  ensureDispatchReady,
  queueEvent,
  shouldIgnoreSocketPayloadWrongAccount,
  shouldPlayInboxAlertForCurrentAccount,
  getCachedUserData,
  inboxCardWorkspaceIdFrom,
  inboxCardConnectionFields,
  resolveIsForActiveChat,
  shouldSocketApplySelectedContactId,
  getTimestampISO,
  shouldAllowRealtimeInboxCardUpdate,
  mergeResolvedSenderFieldsOntoChat,
  applyWorkflowAutomatedMetadata,
  isWhatsAppInboxPlatform,
  transformSocketMessage,
  sanitizeOutboundChatDirection,
} from "./socketSharedState";
import { isConsoleVerbose } from "./consoleGate";

// Bug fix (2026-09-12): a live assignment-count socket event with a garbage
// assignedToId (not a real Mongo ObjectId — e.g. the literal string
// "assignment", seen live on a busy account) used to fall through to
// updateAssignmentCount's "create new entry" fallback, which pushes a
// placeholder using the raw id AS the display name — corrupting the assign
// dropdown/left-sidebar team list until a full page refresh. Guarding on this
// before any of that logic runs stops it at the source; a valid real
// assignment's id always matches this shape, so this can't affect legitimate
// events, only garbage ones.
const isValidObjectId = (id) => typeof id === 'string' && /^[0-9a-fA-F]{24}$/.test(id);

// Lazily proxies to window.store (see redux/store.js) instead of a static import.
// store.js -> authSlice.js -> socketHandler.js -> this file already form an import
// cycle; importing store here too caused an intermittent "Cannot access
// '__WEBPACK_DEFAULT_EXPORT__' before initialization" crash depending on which
// module the bundler happened to touch first. Every call site below only ever
// calls store.getState() as a plain closure (no `this` reliance), so a lazy
// proxy is a drop-in replacement for the static import.
const store = new Proxy({}, {
  get: (_, prop) => (typeof window !== 'undefined' && window.store ? window.store[prop] : undefined),
});

// "Assigned to me" for notification purposes covers contacts assigned to the
// user directly (contactAssigned) as well as contacts assigned to any team
// the user is a member of (teamAssignedContactIds).
const isContactAssignedToUserOrTeam = (userDetails, contactId) => {
  const contactIdStr = contactId?.toString ? contactId.toString() : String(contactId);
  const direct = userDetails?.contactAssigned || [];
  const viaTeam = userDetails?.teamAssignedContactIds || [];
  return (
    direct.some((id) => id.toString() === contactIdStr) ||
    viaTeam.some((id) => id.toString() === contactIdStr)
  );
};

// ─── debounced inbox card update ──────────────────────────────────────────────
export const debouncedAddMessageCard = (messageCardData) => {
  const dispatchFn = getDispatch();
  if (!dispatchFn || typeof dispatchFn !== 'function') return;

  const contactId = messageCardData.contactId || messageCardData.contactID;
  const now = Date.now();

  if (store && typeof store.getState === "function") {
    const state = store.getState();
    const currentWs = state.workspace?.currentWorkspace?._id;
    const payloadWs = messageCardData.workspaceId ?? messageCardData.lastMessage?.metadata?.workspace_id;
    if (currentWs != null && payloadWs != null) {
      const a = String(currentWs);
      const b = typeof payloadWs === "object" && payloadWs?.toString ? payloadWs.toString() : String(payloadWs);
      if (a !== b) return;
    }
  }

  if (!shouldAllowRealtimeInboxCardUpdate(messageCardData)) {
    // A reassignment away from this user (server re-adds the previous assignee to the
    // audience for exactly this) — drop the card they can no longer view instead of
    // leaving it stale until a refresh. Other disallowed updates are just ignored.
    if (messageCardData.lastMessage?.type === "assignment") {
      dispatchFn(removeContactFromInbox({ contactId }));
    }
    return;
  }

  const isCloseEvent = messageCardData.status === "Closed" ||
    messageCardData.closeStatus === "Closed" ||
    messageCardData.lastMessage?.type === "closechat" ||
    typeof messageCardData.isBlocked === "boolean";

  // A read-status transition (markMessageAsRead's read_update card, see the isReadUpdate
  // branch above) must never be dropped by the debounce below: unlike a routine content
  // update — where losing one is harmless because the next real message re-syncs the card
  // anyway — there's no follow-up event to correct a swallowed "now read" update. In
  // practice this update almost always lands within DEBOUNCE_TIME of the very message it's
  // marking read (agent reads a chat that's already open the instant it arrives), so without
  // this it was silently dropped on every OTHER connected tab/device, leaving their unread
  // badge stale until a manual refresh or reopening the contact.
  const isReadStatusEvent = messageCardData.markStatus === "read" &&
    messageCardData.lastMessageUnreadByTeam === false;

  // Bug fix, 2026-09-28: this debounce used to key purely on contactId, so two genuinely
  // different real-time events for the same contact arriving within DEBOUNCE_TIME of each
  // other (found via a QR workflow's "Send audio" step immediately followed by its own
  // "Assign conversation" step) got treated as redundant duplicates -- whichever landed
  // second was silently dropped, even though it carried real new content the first one
  // didn't (the assignment pill updated live; the audio message never did, until a reload
  // re-synced from the DB). Not QR-specific in mechanism -- this debounce runs for every
  // provider -- so widening the key here protects Meta/Instagram/Facebook the same way.
  // Including the event's own type/content/timestamp keeps the original protection (an
  // identical event redelivered twice, e.g. a reconnect replay, still collapses to one
  // dispatch) while letting two distinct updates for the same contact both go through.
  const lastMessageForKey = messageCardData.lastMessage;
  const contentFingerprint = lastMessageForKey
    ? `${lastMessageForKey.type || ""}:${lastMessageForKey.content ?? lastMessageForKey.body ?? ""}:${lastMessageForKey.timestamp || ""}`
    : "";
  const dedupeKey = contactId ? `${contactId}:${contentFingerprint}` : null;

  if (!isCloseEvent && !isReadStatusEvent && dedupeKey) {
    const lastUpdate = socketState.lastMessageCardUpdate.get(dedupeKey);
    if (lastUpdate && (now - lastUpdate) < DEBOUNCE_TIME) return;
  }

  if (dedupeKey) {
    // The key space grew from "one entry per contact" to "one entry per (contact, distinct
    // content) pair" above, so this map can now accumulate for the life of the tab instead of
    // staying naturally bounded by contact count. Every entry is dead weight past DEBOUNCE_TIME
    // anyway (it can never match the check above again), so a simple size-capped reset is a
    // safe, cheap bound -- worst case it forgets a just-set entry a little early, which only
    // means one update that could have been debounced isn't, never a dropped one.
    if (socketState.lastMessageCardUpdate.size > 1000) socketState.lastMessageCardUpdate.clear();
    socketState.lastMessageCardUpdate.set(dedupeKey, now);
  }
  dispatchFn(addMessageCard(messageCardData));
};

// ─── new inbox message handler ────────────────────────────────────────────────
export const handleNewInboxMessage = (message) => {
  console.log(`🔍 [NEW_INBOX_MESSAGE] Event received:`, {
    contactId: message.contactId || message.contactID,
    messageId: message.message?.id || message.message?.tempId || message.chat?.id || message.chat?.tempId,
    type: message.message?.type || message.chat?.type,
    status: message.message?.status || message.chat?.status,
    isInternalMention: message.message?.isInternalMention || message.chat?.isInternalMention,
    sendername: message.message?.sendername || message.chat?.sendername,
    senderid: message.message?.senderid || message.chat?.senderid,
    body: message.message?.body || message.chat?.body,
    message: message.message?.message || message.chat?.message
  });

  if (!ensureDispatchReady()) {
    queueEvent('new_inbox_message', message, handleNewInboxMessage);
    return;
  }

  try {
    const dispatchFn = getDispatch();
    const contactId = message.contactId || message.contactID;
    if (!contactId) return;
    if (shouldIgnoreSocketPayloadWrongAccount(message)) return;

    // Handle read_update messages
    const isReadUpdate = (message.message && message.message.type === "read_update") ||
      (message.chat && message.chat.type === "read_update") ||
      (message.lastMessage && message.lastMessage.type === "read_update");
    if (isReadUpdate) {
      const readUpWs = inboxCardWorkspaceIdFrom(message);
      const readUpdatePayload = {
        contactId, contactID: contactId,
        receivedCount: 0, unreadCount: 0, markStatus: "read", lastMessageUnreadByTeam: false,
        ...(readUpWs != null && readUpWs !== "" && { workspaceId: readUpWs }),
        ...inboxCardConnectionFields(message),
      };
      debouncedAddMessageCard(readUpdatePayload);
      const readUpdateChats = message.message?.chats;
      if (readUpdateChats && Array.isArray(readUpdateChats)) {
        readUpdateChats.forEach(chat => {
          const chatMessageId = chat.id || chat._id || chat.tempId;
          const isInternalMention = chat.isInternalMention || chat.status === "internal";
          if (isInternalMention) return;
          const isOutgoingMessage = !!chat.senderid;
          if (!isOutgoingMessage && dispatchFn && typeof dispatchFn === 'function' && chatMessageId) {
            dispatchFn(updateMessageStatus({
              contactId: contactId ? String(contactId) : contactId,
              messageId: chatMessageId ? String(chatMessageId) : chatMessageId,
              status: "read", timestamp: chat.timestamp
            }));
          }
        });
      }
      return;
    }

    // Normalize message
    let normalizedMessage = { ...message, contactId, contactID: contactId };

    if (message.message && typeof message.message === 'object') {
      normalizedMessage.chat = { ...message.message };
      if (!normalizedMessage.chat.senderAvatarColor && message.chat?.senderAvatarColor) normalizedMessage.chat.senderAvatarColor = message.chat.senderAvatarColor;
      if (!normalizedMessage.chat.sendername && message.chat?.sendername) normalizedMessage.chat.sendername = message.chat.sendername;
      if (message.chat?.isInternalMention !== undefined && normalizedMessage.chat.isInternalMention === undefined) normalizedMessage.chat.isInternalMention = message.chat.isInternalMention;
      if (message.chat?.status === "internal" && normalizedMessage.chat.status !== "internal") { normalizedMessage.chat.status = "internal"; normalizedMessage.chat.isInternalMention = true; }
      if (message.message.replySenderName !== undefined && message.message.replySenderName !== null && message.message.replySenderName !== "") normalizedMessage.chat.replySenderName = message.message.replySenderName;
      if (message.message.replyBody !== undefined) normalizedMessage.chat.replyBody = message.message.replyBody;
      if (message.message.replyType !== undefined) normalizedMessage.chat.replyType = message.message.replyType;
      if (message.message.replyId !== undefined) normalizedMessage.chat.replyId = message.message.replyId;
      if (message.message.replyTo !== undefined) { normalizedMessage.chat.replyTo = message.message.replyTo; if (!normalizedMessage.chat.replyId) normalizedMessage.chat.replyId = normalizedMessage.chat.replyTo; }
    } else if (message.chat && typeof message.chat === 'object') {
      normalizedMessage.chat = { ...message.chat };
      if (message.chat.replySenderName !== undefined && message.chat.replySenderName !== null) normalizedMessage.chat.replySenderName = message.chat.replySenderName;
      if (message.chat.replyBody !== undefined) normalizedMessage.chat.replyBody = message.chat.replyBody;
      if (message.chat.replyType !== undefined) normalizedMessage.chat.replyType = message.chat.replyType;
      if (message.chat.replyId !== undefined) normalizedMessage.chat.replyId = message.chat.replyId;
      if (message.chat.replyTo !== undefined) { normalizedMessage.chat.replyTo = message.chat.replyTo; if (!normalizedMessage.chat.replyId) normalizedMessage.chat.replyId = normalizedMessage.chat.replyTo; }
    }

    if (message.chatType && !message.chat && message.fullMessage) {
      if (message.fullMessage.chat) {
        normalizedMessage.chat = { ...message.fullMessage.chat };
        if (message.fullMessage.chat.replySenderName !== undefined) normalizedMessage.chat.replySenderName = message.fullMessage.chat.replySenderName;
        if (message.fullMessage.chat.replyBody !== undefined) normalizedMessage.chat.replyBody = message.fullMessage.chat.replyBody;
        if (message.fullMessage.chat.replyType !== undefined) normalizedMessage.chat.replyType = message.fullMessage.chat.replyType;
        if (message.fullMessage.chat.replyId !== undefined) normalizedMessage.chat.replyId = message.fullMessage.chat.replyId;
        if (message.fullMessage.chat.replyTo !== undefined) { normalizedMessage.chat.replyTo = message.fullMessage.chat.replyTo; if (!normalizedMessage.chat.replyId) normalizedMessage.chat.replyId = normalizedMessage.chat.replyTo; }
        if (normalizedMessage.chat && !normalizedMessage.chat.createdAt) {
          const ts = normalizedMessage.chat.timestamp || normalizedMessage.chat.time;
          normalizedMessage.chat.createdAt = ts ? (typeof ts === 'string' ? ts : new Date(ts).toISOString()) : new Date().toISOString();
        } else if (normalizedMessage.chat && normalizedMessage.chat.createdAt instanceof Date) {
          normalizedMessage.chat.createdAt = normalizedMessage.chat.createdAt.toISOString();
        } else if (normalizedMessage.chat && typeof normalizedMessage.chat.createdAt !== 'string') {
          normalizedMessage.chat.createdAt = new Date(normalizedMessage.chat.createdAt).toISOString();
        }
        normalizedMessage = {
          ...normalizedMessage, ...message.fullMessage,
          chat: normalizedMessage.chat,
          chatType: message.chatType || normalizedMessage.chat?.type || normalizedMessage.chat?.messageType || "chat",
          phoneNumber: message.fullMessage.phoneNumber || message.fullMessage.phoneNumberID,
          phoneNumberID: message.fullMessage.phoneNumberID || message.fullMessage.phoneNumber,
          name: message.fullMessage.name || normalizedMessage.name,
          contactName: message.fullMessage.name || normalizedMessage.name,
          status: message.fullMessage.status || normalizedMessage.status,
          hoursRemaining: message.fullMessage.hoursRemaining || message.fullMessage.hoursLeft,
          hoursLeft: message.fullMessage.hoursLeft || message.fullMessage.hoursRemaining
        };
      }
      if (!normalizedMessage.chat?.replySenderName && message.chat?.replySenderName) {
        if (!normalizedMessage.chat) normalizedMessage.chat = {};
        normalizedMessage.chat.replySenderName = message.chat.replySenderName;
      }
      if (!normalizedMessage.chat?.replySenderName && message.fullMessage?.chat?.replySenderName) {
        if (!normalizedMessage.chat) normalizedMessage.chat = {};
        normalizedMessage.chat.replySenderName = message.fullMessage.chat.replySenderName;
      }
    } else {
      normalizedMessage = {
        ...normalizedMessage, ...message.fullMessage,
        chatType: message.chatType || normalizedMessage.chat?.type || normalizedMessage.chat?.messageType || "chat",
        contactName: message.fullMessage?.name || normalizedMessage.name
      };
    }

    // If chat still missing, construct from lastMessage or chatType
    if (!normalizedMessage.chat) {
      if (normalizedMessage.lastMessage) {
        normalizedMessage.chat = {
          type: normalizedMessage.chatType || normalizedMessage.lastMessage.type || "chat",
          messageType: normalizedMessage.chatType || normalizedMessage.lastMessage.type || "chat",
          body: normalizedMessage.lastMessage.content || "",
          message: normalizedMessage.lastMessage.content || "",
          timestamp: normalizedMessage.lastMessage.timestamp ? (typeof normalizedMessage.lastMessage.timestamp === 'string' ? normalizedMessage.lastMessage.timestamp : new Date(normalizedMessage.lastMessage.timestamp).toISOString()) : new Date().toISOString(),
          time: normalizedMessage.lastMessage.timestamp ? (typeof normalizedMessage.lastMessage.timestamp === 'string' ? normalizedMessage.lastMessage.timestamp : new Date(normalizedMessage.lastMessage.timestamp).toISOString()) : new Date().toISOString(),
          createdAt: normalizedMessage.lastMessage.timestamp ? (typeof normalizedMessage.lastMessage.timestamp === 'string' ? normalizedMessage.lastMessage.timestamp : new Date(normalizedMessage.lastMessage.timestamp).toISOString()) : new Date().toISOString(),
          status: "sent", from: normalizedMessage.phoneNumberID || normalizedMessage.phoneNumber, to: null
        };
      } else if (normalizedMessage.chatType) {
        normalizedMessage.chat = {
          type: normalizedMessage.chatType === "list" ? "chat" : normalizedMessage.chatType,
          messageType: normalizedMessage.chatType,
          body: normalizedMessage.body || "",
          message: normalizedMessage.body || "",
          timestamp: normalizedMessage.timestamp ? (typeof normalizedMessage.timestamp === 'string' ? normalizedMessage.timestamp : new Date(normalizedMessage.timestamp).toISOString()) : new Date().toISOString(),
          time: normalizedMessage.timestamp ? (typeof normalizedMessage.timestamp === 'string' ? normalizedMessage.timestamp : new Date(normalizedMessage.timestamp).toISOString()) : new Date().toISOString(),
          createdAt: normalizedMessage.createdAt ? (typeof normalizedMessage.createdAt === 'string' ? normalizedMessage.createdAt : new Date(normalizedMessage.createdAt).toISOString()) : new Date().toISOString(),
          status: "sent", from: normalizedMessage.from || normalizedMessage.phoneNumberID, to: normalizedMessage.to || null,
          replySenderName: normalizedMessage.replySenderName || normalizedMessage.chat?.replySenderName || "",
          replyBody: normalizedMessage.replyBody || normalizedMessage.chat?.replyBody || "",
          replyType: normalizedMessage.replyType || normalizedMessage.chat?.replyType || "",
          replyId: normalizedMessage.replyId || normalizedMessage.chat?.replyId || normalizedMessage.replyTo || normalizedMessage.chat?.replyTo || "",
          replyTo: normalizedMessage.replyTo || normalizedMessage.chat?.replyTo || ""
        };
      }
    }

    if (!normalizedMessage.chat && (normalizedMessage.chatType || message.fullMessage)) {
      if (message.fullMessage && message.fullMessage.chat) {
        normalizedMessage.chat = { ...message.fullMessage.chat };
      } else if (normalizedMessage.chatType) {
        const getISOString = (ts) => {
          if (!ts) return new Date().toISOString();
          if (typeof ts === 'string' && ts.includes('T')) return ts;
          if (ts instanceof Date) return ts.toISOString();
          return new Date(ts).toISOString();
        };
        const _platSynthetic = normalizedMessage.platform ?? message?.platform ?? "whatsapp";
        const _synCh = message.fullMessage?.chat;
        const _synHasSendername = _synCh?.sendername != null && _synCh?.sendername !== "";
        const _synPerformedBy = _synCh?.performedBy || null;
        // Only apply legacy isWorkflowAutomated when performedBy is absent
        const _synWorkflowMeta = !_synPerformedBy && isWhatsAppInboxPlatform(_platSynthetic) && !_synHasSendername && !_synCh?.senderid && !_synCh?.clientid && !_synCh?.clientname ? { isWorkflowAutomated: true } : {};
        normalizedMessage.chat = {
          type: normalizedMessage.chatType === "list" ? "chat" : normalizedMessage.chatType,
          messageType: normalizedMessage.chatType,
          body: message.fullMessage?.chat?.body || message.body || "",
          timestamp: getISOString(message.fullMessage?.chat?.timestamp || message.timestamp),
          time: getISOString(message.fullMessage?.chat?.time || message.fullMessage?.chat?.timestamp || message.timestamp),
          createdAt: getISOString(message.fullMessage?.chat?.createdAt || message.fullMessage?.chat?.timestamp || message.timestamp),
          status: message.fullMessage?.chat?.status || "sent",
          from: message.fullMessage?.chat?.from || normalizedMessage.phoneNumberID,
          to: message.fullMessage?.chat?.to || null,
          senderid: message.fullMessage?.chat?.senderid || null,
          clientid: message.fullMessage?.chat?.clientid || null,
          clientname: message.fullMessage?.chat?.clientname || null,
          sendername: _synHasSendername ? _synCh.sendername : isWhatsAppInboxPlatform(_platSynthetic) ? undefined : "Automated",
          _id: message.fullMessage?.chat?._id || message.fullMessage?.chat?.id || Date.now().toString(),
          id: message.fullMessage?.chat?.id || message.fullMessage?.chat?._id || Date.now().toString(),
          ..._synWorkflowMeta,
          ...(_synPerformedBy && { performedBy: _synPerformedBy }),
          ...(_synCh?.performedByName && { performedByName: _synCh.performedByName }),
        };
      }
    }

    // Ensure messageType set correctly
    if (normalizedMessage.chat) {
      if (normalizedMessage.chatType) {
        normalizedMessage.chat.messageType = normalizedMessage.chatType;
        if (normalizedMessage.chatType === "list" && normalizedMessage.chat.type !== "chat") normalizedMessage.chat.type = "chat";
      } else if (!normalizedMessage.chat.messageType && normalizedMessage.chat.type) {
        normalizedMessage.chat.messageType = normalizedMessage.chat.type;
      } else if (!normalizedMessage.chat.messageType && !normalizedMessage.chat.type) {
        normalizedMessage.chat.messageType = "text"; normalizedMessage.chat.type = "chat";
      }
    }

    const isForActiveChat = resolveIsForActiveChat(contactId, normalizedMessage, message, message.fullMessage);

    // Assignment messages
    if (normalizedMessage.chat && normalizedMessage.chat.type === "assignment") {
      // Same fetchContactById cache-bust as socketMessageDispatcher.js's own copy of this
      // block (receive_message handler) — this new_inbox_message handler is an independent
      // duplicate that fires for the same assignment event, and had the same gap: it only
      // patches contactInfo in Redux when the contact is already the active chat, so opening
      // the chat right after (e.g. a call-answer flow navigating straight in) could still
      // serve a pre-assignment snapshot from the cache. Harmless if the other handler already
      // invalidated it — this is a plain cache delete, safe to call twice.
      invalidateCachedContact(contactId);

      const isUnassignment = normalizedMessage.chat.body === "unassigned" || normalizedMessage.chat.body === "Unassigned" ||
        normalizedMessage.chat.body?.toLowerCase() === "unassigned" || !normalizedMessage.chat.to ||
        normalizedMessage.chat.to === "" || normalizedMessage.chat.to === "Assignment" || normalizedMessage.chat.status === "unassigned";
      const assignedToId = message?.assignedTo || message?.assignedToId || normalizedMessage.assignedTo || normalizedMessage.assignedToId || normalizedMessage.chat?.to;
      let assignedToName = message?.assignedToName || normalizedMessage.assignedToName || normalizedMessage.chat?.toName || normalizedMessage.teamName;
      if (!assignedToName || assignedToName === assignedToId) {
        assignedToName = normalizedMessage.teamName || normalizedMessage.chat?.teamName || message?.teamName || message?.team?.name || assignedToName;
      }
      // AI Takeover assignee flag (see AI Takeover / aiTakeoverEventHandler.js) — was
      // dropped here even after socketHandler.js started forwarding it on the raw
      // socket payload: this object is hand-built field-by-field, not a spread of
      // message/normalizedMessage, so a field missing from this list never reaches
      // addMessageCard/updateTeamAssignment below no matter what the server sends
      // (Rahim, 2026-09-01: "the name in messageCard didn't display at runtime").
      const assignedToIsAi = message?.assignedToIsAi === true || normalizedMessage.assignedToIsAi === true;

      const assignInboxWs = inboxCardWorkspaceIdFrom(normalizedMessage, message);
      const assignmentMessagePayload = {
        contactId, contactID: contactId,
        name: normalizedMessage.name || "Unknown",
        phoneNumber: normalizedMessage.phoneNumber || normalizedMessage.phoneNumberID,
        status: isUnassignment ? "Unassigned" : "Assigned",
        assignedTo: isUnassignment ? null : assignedToId,
        assignedToName: isUnassignment ? null : assignedToName,
        assignedToIsAi: isUnassignment ? false : assignedToIsAi,
        // No `content` here on purpose: assignment is a log event, not a sent/received
        // message, so the inbox preview must keep showing the last real chat message.
        // `type`/`body` are kept for status-detection logic elsewhere in the reducer.
        lastMessage: {
          timestamp: getTimestampISO(normalizedMessage.chat.timestamp || normalizedMessage.chat.time),
          type: "assignment",
          body: normalizedMessage.chat.body || (isUnassignment ? "unassigned" : "Assigned"),
        },
        activeStatus: normalizedMessage.hoursRemaining || normalizedMessage.hoursLeft || 0,
        hoursRemaining: normalizedMessage.hoursRemaining || normalizedMessage.hoursLeft || 0,
        receivedCount: 0, labels: normalizedMessage.labels || [],
        ...(assignInboxWs != null && assignInboxWs !== "" && { workspaceId: assignInboxWs }),
        ...inboxCardConnectionFields(normalizedMessage, message),
      };
      debouncedAddMessageCard(assignmentMessagePayload);

      if (dispatchFn && store && typeof store.getState === "function") {
        const state = store.getState();
        const currentContactInfo = state?.inboxInfo?.contactInfo;
        const selectedContactId = state?.inboxAll?.selectedContactID;
        const isContactBeingViewed = (currentContactInfo && currentContactInfo._id === contactId) ||
          (selectedContactId === contactId) || resolveIsForActiveChat(contactId, normalizedMessage, message, message.fullMessage);
        if (isContactBeingViewed) {
          const contactInfoPayload = {
            _id: contactId,
            status: isUnassignment ? "Unassigned" : "Assigned",
            assignedTo: isUnassignment ? null : assignedToId,
            assignedToName: isUnassignment ? null : assignedToName,
            assignedToId: isUnassignment ? null : assignedToId,
            name: normalizedMessage.name || currentContactInfo?.name || "Unknown",
            phoneNumber: normalizedMessage.phoneNumber || normalizedMessage.phoneNumberID || currentContactInfo?.phoneNumber,
            ...(currentContactInfo && currentContactInfo._id === contactId ? {
              labels: currentContactInfo.labels || [],
              hoursRemaining: currentContactInfo.hoursRemaining,
              ...(normalizedMessage.hoursRemaining !== undefined && { hoursRemaining: normalizedMessage.hoursRemaining }),
              alwaysAssignedEnabled: currentContactInfo.alwaysAssignedEnabled || false,
              alwaysAssignedTo: currentContactInfo.alwaysAssignedTo || null,
              alwaysAssignedToType: currentContactInfo.alwaysAssignedToType || null
            } : {})
          };
          if (dispatchFn && typeof dispatchFn === 'function') {
            const contactName = normalizedMessage?.contactName || normalizedMessage?.name || message?.fullMessage?.name || message?.name || contactInfoPayload.name;
            batch(() => {
              dispatchFn({ type: 'inboxInfo/setContactInfo', payload: contactInfoPayload });
              if (contactName) dispatchFn(updateContactName({ contactId, name: contactName }));
            });
          }
        }
      }

      if (dispatchFn && store && typeof store.getState === "function") {
        setTimeout(() => {
          const state = store.getState();
          if (state && state.inbox && state.inbox.data) calculateAndDispatchInboxCounts(dispatchFn, state.inbox.data);
        }, 100);
      }

      if (dispatchFn && store && typeof store.getState === "function" && assignedToId) {
        let assignedToType = message?.assignedToType || normalizedMessage?.assignedToType || null;
        if (assignedToType === 'teammember' || assignedToType === 'member') assignedToType = 'member';
        else if (assignedToType === 'team') assignedToType = 'team';
        // AI Takeover assignee — never a team/team-member headcount, so it must never
        // fall into the "default to member" branch below (see AI Takeover /
        // aiTakeoverEventHandler.js), or it pollutes inboxSettings.teamMembers with a
        // fake entry keyed by the agent's id. assignedToIsAi is the direct signal the
        // backend already sends (enableTakeover / assignContact.service.js); the
        // aiAgents roster lookup is a fallback for payloads that predate that flag.
        const isAiAssignee = message?.assignedToIsAi || normalizedMessage?.assignedToIsAi || false;
        if (!assignedToType) {
          const globalState = store.getState();
          const inboxSettings = globalState?.inboxSettings;
          if (isAiAssignee || globalState?.aiAgents?.agents?.some(a => String(a._id || a.id) === String(assignedToId))) {
            assignedToType = 'agent';
          } else if (inboxSettings?.teams?.some(t => String(t._id || t.id) === String(assignedToId))) {
            assignedToType = 'team';
          } else if (inboxSettings?.teamMembers?.some(m => String(m._id || m.id) === String(assignedToId))) {
            assignedToType = 'member';
          }
          if (!assignedToType) assignedToType = 'member';
        }
        const increment = isUnassignment ? -1 : 1;
        // 'agent' has no headcount bucket in inboxSettings (teamMembers/teams) — skip
        // the dispatch entirely rather than let it fall through and create/corrupt one.
        // isValidObjectId guard (2026-09-12): only skips THIS dispatch (the one that
        // can create a garbage placeholder) for a malformed assignedToId — deliberately
        // scoped this narrow, not wrapped around the whole outer `if`, so the
        // oldAssignedTo decrement logic below (a separate, valid operation) still runs
        // even when the NEW assignedToId happens to be bad.
        if (assignedToType && assignedToType !== 'agent' && isValidObjectId(assignedToId) && dispatchFn) dispatchFn(updateAssignmentCount({ assignedToId, assignedToType, increment }));
        if (!isUnassignment && normalizedMessage.contact) {
          const oldAssignedTo = normalizedMessage.contact.assignedTo || normalizedMessage.contact.assignedToId;
          if (oldAssignedTo && String(oldAssignedTo) !== String(assignedToId)) {
            let oldAssignedToType = normalizedMessage.contact.assignedToType || null;
            if (oldAssignedToType === 'teammember' || oldAssignedToType === 'member') oldAssignedToType = 'member';
            else if (oldAssignedToType === 'team') oldAssignedToType = 'team';
            if (!oldAssignedToType) {
              const gs = store.getState(); const is = gs?.inboxSettings;
              if (is?.teams?.some(t => String(t._id || t.id) === String(oldAssignedTo))) oldAssignedToType = 'team';
              else if (is?.teamMembers?.some(m => String(m._id || m.id) === String(oldAssignedTo))) oldAssignedToType = 'member';
            }
            if (oldAssignedToType && dispatchFn) dispatchFn(updateAssignmentCount({ assignedToId: oldAssignedTo, assignedToType: oldAssignedToType, increment: -1 }));
          }
        }
      }

      const assignmentEventData = { contactId, contactID: contactId, status: isUnassignment ? "Unassigned" : "Assigned", assignedTo: isUnassignment ? null : assignedToId, assignedToName: isUnassignment ? null : assignedToName, assignedToId: isUnassignment ? null : assignedToId };
      window.dispatchEvent(new CustomEvent('assignmentUpdate', { detail: assignmentEventData }));
      window.dispatchEvent(new CustomEvent('contactStatusUpdate', { detail: assignmentEventData }));

      if (dispatchFn && typeof dispatchFn === 'function') {
        dispatchFn(setSelectedSettings("All"));
        if (isForActiveChat && shouldSocketApplySelectedContactId(message.contactId)) dispatchFn(setselectedContactID(message.contactId));
      }
      return;
    }

    // Closechat messages
    if (message.chat && message.chat.type === "closechat") {
      const normalizedCloseStatus = message.status || "Closed";
      const closeInboxWs = inboxCardWorkspaceIdFrom(message);
      const closeMessagePayload = {
        contactId: message.contactId, contactID: message.contactId,
        name: message.name || "Unknown", phoneNumber: message.phoneNumber || message.phoneNumberID,
        status: normalizedCloseStatus, closeStatus: normalizedCloseStatus,
        assignedTo: null, assignedToName: null,
        // Closing always ends AI Takeover (see closedContact.service.js) — explicit
        // false so a live close push clears a stale "(AI)" badge on this card too.
        assignedToIsAi: false,
        previousAssignedToId: message.previousAssignedToId, previousAssignedToType: message.previousAssignedToType,
        // No `content`: closechat is a log event, not a sent/received message — keep
        // showing the last real chat message in the inbox preview instead of this text.
        lastMessage: { timestamp: getTimestampISO(message.chat.timestamp || message.chat.time), type: "closechat", body: normalizedCloseStatus },
        activeStatus: message.hoursRemaining || message.hoursLeft || 0, hoursRemaining: message.hoursRemaining || message.hoursLeft || 0,
        receivedCount: 0, labels: message.labels || [],
        ...(closeInboxWs != null && closeInboxWs !== "" && { workspaceId: closeInboxWs }),
        ...inboxCardConnectionFields(message),
      };
      if (message.previousAssignedToId && message.previousAssignedToType && dispatchFn && typeof dispatchFn === 'function') {
        dispatchFn(removeContactFromAssignment({ contactId: message.contactId, assignedToId: message.previousAssignedToId, assignedToType: message.previousAssignedToType }));
      }
      debouncedAddMessageCard(closeMessagePayload);
      if (dispatchFn && store && typeof store.getState === "function") {
        const state = store.getState();
        const currentContactInfo = state?.inboxInfo?.contactInfo;
        if (currentContactInfo && currentContactInfo._id === message.contactId) {
          const contactInfoPayload = { _id: message.contactId, status: normalizedCloseStatus, assignedTo: null, assignedToName: null, assignedToId: null, name: message.name || currentContactInfo.name || "Unknown", phoneNumber: message.phoneNumber || message.phoneNumberID || currentContactInfo.phoneNumber };
          if (dispatchFn && typeof dispatchFn === 'function') dispatchFn({ type: 'inboxInfo/setContactInfo', payload: contactInfoPayload });
        }
      }
      if (dispatchFn && store && typeof store.getState === "function") {
        setTimeout(() => {
          const state = store.getState();
          if (state && state.inbox && state.inbox.data) calculateAndDispatchInboxCounts(dispatchFn, state.inbox.data);
        }, 100);
      }
      const closeEventData = { contactId: message.contactId, contactID: message.contactId, status: normalizedCloseStatus, closeStatus: normalizedCloseStatus, assignedTo: null, assignedToName: null, assignedToId: null };
      window.dispatchEvent(new CustomEvent('closeUpdate', { detail: closeEventData }));
      window.dispatchEvent(new CustomEvent('contactStatusUpdate', { detail: closeEventData }));
      return;
    }

    // Block/unblock messages — same card-relocation treatment as closechat above.
    // This handler (new_inbox_message) runs independently of socketMessageDispatcher.js's
    // handleReceiveMessage (receive_message) — the server broadcasts both events for every
    // block/unblock, and each has its own duplicated closechat-style relocation logic, so
    // blockcontact needs its own branch here too or this handler silently drops the update.
    if (message.chat && message.chat.type === "blockcontact") {
      const isUnblocked = typeof message.chat.body === 'string' && message.chat.body.toLowerCase() === 'unblocked';
      const blockInboxWs = inboxCardWorkspaceIdFrom(message);
      const blockMessagePayload = {
        contactId: message.contactId, contactID: message.contactId,
        name: message.name || "Unknown",
        isBlocked: !isUnblocked,
        // No `content`: this is a log event, not a sent/received message — keep showing
        // the last real chat message in the inbox preview instead of this text.
        lastMessage: { timestamp: getTimestampISO(message.chat.timestamp || message.chat.time), type: "blockcontact", body: message.chat.body },
        ...(blockInboxWs != null && blockInboxWs !== "" && { workspaceId: blockInboxWs }),
        ...inboxCardConnectionFields(message),
      };
      debouncedAddMessageCard(blockMessagePayload);
      return;
    }

    // Regular messages
    let messageChat = normalizedMessage.chat;
    if (!messageChat && normalizedMessage.message && typeof normalizedMessage.message === 'object') {
      messageChat = normalizedMessage.message; normalizedMessage.chat = messageChat;
    }
    const messageType = messageChat?.type || normalizedMessage.chatType || normalizedMessage.chat?.messageType;
    const isValidMessageType = messageType && ["chat", "audio", "video", "document", "image", "list", "text", "template", "catalog", "catalog_purchase", "story_reply", "post_comment", "live_comment", "instagram_reaction", "facebook_reaction"].includes(messageType);
    const hasChatType = normalizedMessage.chatType && ["chat", "audio", "video", "document", "image", "list", "text", "template", "catalog", "catalog_purchase", "story_reply", "post_comment", "live_comment", "instagram_reaction", "facebook_reaction"].includes(normalizedMessage.chatType);

    if ((messageChat && isValidMessageType) || hasChatType) {
      const isFromContact = (messageChat?.clientid || normalizedMessage.clientid || messageChat?.clientname || normalizedMessage.clientname) && !(messageChat?.senderid || normalizedMessage.senderid || messageChat?.sendername || normalizedMessage.sendername);

      if (!messageChat && hasChatType) {
        messageChat = {
          type: normalizedMessage.chatType === "list" ? "chat" : normalizedMessage.chatType,
          messageType: normalizedMessage.chatType,
          body: normalizedMessage.body || normalizedMessage.fullMessage?.lastMessage?.content || "",
          message: normalizedMessage.body || normalizedMessage.fullMessage?.lastMessage?.content || "",
          timestamp: normalizedMessage.timestamp ? (typeof normalizedMessage.timestamp === 'string' ? normalizedMessage.timestamp : new Date(normalizedMessage.timestamp).toISOString()) : new Date().toISOString(),
          time: normalizedMessage.timestamp ? (typeof normalizedMessage.timestamp === 'string' ? normalizedMessage.timestamp : new Date(normalizedMessage.timestamp).toISOString()) : new Date().toISOString(),
          createdAt: normalizedMessage.createdAt ? (typeof normalizedMessage.createdAt === 'string' ? normalizedMessage.createdAt : new Date(normalizedMessage.createdAt).toISOString()) : new Date().toISOString(),
          status: "sent", from: normalizedMessage.from || normalizedMessage.phoneNumberID || normalizedMessage.phoneNumber, to: normalizedMessage.to || null,
          replyId: normalizedMessage.replyId || normalizedMessage.replyTo || normalizedMessage.chat?.replyId || normalizedMessage.chat?.replyTo,
          replyTo: normalizedMessage.replyTo || normalizedMessage.replyId || normalizedMessage.chat?.replyTo || normalizedMessage.chat?.replyId,
          replySenderName: normalizedMessage.replySenderName || normalizedMessage.chat?.replySenderName,
          replyBody: normalizedMessage.replyBody || normalizedMessage.chat?.replyBody,
          replyType: normalizedMessage.replyType || normalizedMessage.chat?.replyType
        };
        normalizedMessage.chat = messageChat;
      }
      if (!normalizedMessage.chatType) normalizedMessage.chatType = messageChat?.type || messageChat?.messageType || message?.chatType || "chat";
      const actualType = messageChat?.type || normalizedMessage.chatType || "chat";
      if (!normalizedMessage.contactName) normalizedMessage.contactName = normalizedMessage.name || message.fullMessage?.name || message.name || "Unknown";

      const resolvedLastMsgSenderId = messageChat?.senderid || normalizedMessage.senderid || normalizedMessage.chat?.senderid || message?.fullMessage?.chat?.senderid || message?.fullMessage?.message?.senderid;
      const resolvedLastMsgSenderName = messageChat?.sendername || normalizedMessage.sendername || normalizedMessage.chat?.sendername || message?.fullMessage?.chat?.sendername || message?.fullMessage?.message?.sendername;
      const isWorkflowMessageForLastMessage = !resolvedLastMsgSenderId && !resolvedLastMsgSenderName && !(messageChat?.clientid || normalizedMessage.clientid || normalizedMessage.chat?.clientid || message?.fullMessage?.chat?.clientid) && !(messageChat?.clientname || normalizedMessage.clientname || normalizedMessage.chat?.clientname || message?.fullMessage?.chat?.clientname);
      const isInternalMentionForRecent = messageChat?.isInternalMention || messageChat?.status === "internal" || normalizedMessage.chat?.isInternalMention || normalizedMessage.chat?.status === "internal";

      console.log(`🔍 [NEW_INBOX_MESSAGE] Internal mention check:`, { isInternalMentionForRecent, messageChatIsInternalMention: messageChat?.isInternalMention, messageChatStatus: messageChat?.status, isForActiveChat });

      const lastMsgPlatform = normalizedMessage.platform ?? message?.platform ?? "whatsapp";
      let lastMessageSendername = resolvedLastMsgSenderName || messageChat?.sendername || normalizedMessage.chat?.sendername || normalizedMessage.sendername || null;
      let lastMessageIsWorkflowAutomated = false;
      // Resolve performedBy from the most specific source available
      const resolvedPerformedBy = messageChat?.performedBy || normalizedMessage.chat?.performedBy || normalizedMessage.performedBy || message?.fullMessage?.chat?.performedBy || null;
      const resolvedPerformedByName = messageChat?.performedByName || normalizedMessage.chat?.performedByName || normalizedMessage.performedByName || null;
      // Only fall back to legacy isWorkflowAutomated when performedBy is absent (old messages)
      if (isWorkflowMessageForLastMessage && !lastMessageSendername && !resolvedPerformedBy) {
        if (lastMsgPlatform === undefined || lastMsgPlatform === null || lastMsgPlatform === "whatsapp" || lastMsgPlatform === "qr") { lastMessageSendername = null; lastMessageIsWorkflowAutomated = true; }
        else lastMessageSendername = "Automated";
      }

      const chatInboxWs = inboxCardWorkspaceIdFrom(normalizedMessage, message, message.fullMessage);
      let lastMessageFromContactFlag = false; let lastMessageUnreadByTeamFlag = false;
      const workflowTreatsAsNonCustomer = isWorkflowMessageForLastMessage && !isInternalMentionForRecent;
      if (!workflowTreatsAsNonCustomer && store && typeof store.getState === "function") {
        try {
          const state = store.getState();
          const existingInbox = findInboxContactInData(state?.inbox?.data, contactId);
          const contactLike = buildContactLikeForSocket(existingInbox, normalizedMessage);
          const threadFlags = computeLastMessageThreadFlagsFromChat(messageChat, contactLike);
          lastMessageFromContactFlag = threadFlags.lastMessageFromContact;
          lastMessageUnreadByTeamFlag = Boolean(threadFlags.lastMessageUnreadByTeam) && !isForActiveChat;
        } catch (_) { lastMessageFromContactFlag = false; lastMessageUnreadByTeamFlag = false; }
      }
      if (!lastMessageFromContactFlag && isFromContact && !workflowTreatsAsNonCustomer) {
        lastMessageFromContactFlag = true;
        const st = messageChat?.status;
        lastMessageUnreadByTeamFlag = (st === "received" || st === "delivered") && st !== "read" && st !== "seen" && !isForActiveChat;
      }

      const catalogOrderPreview = "🛒 Product Order";
      const rawChatBody = messageChat?.body || messageChat?.message || normalizedMessage.chat?.body || normalizedMessage.chat?.message || "";
      let isProductCatalogInboxPreview = messageChat?.messageType === "catalog" || messageChat?.messageType === "catalog_purchase";
      if (!isProductCatalogInboxPreview && actualType === "chat" && typeof rawChatBody === "string" && rawChatBody.trim().startsWith("{")) {
        try { const parsed = JSON.parse(rawChatBody); if (parsed && typeof parsed === "object" && (parsed.catalogType || parsed.productData)) isProductCatalogInboxPreview = true; } catch (_) {}
      }

      const chatMessagePayload = {
        contactID: contactId,
        name: normalizedMessage.contactName || normalizedMessage.name || message.fullMessage?.name || message.name || "Unknown",
        ...(!isInternalMentionForRecent && {
          status: normalizedMessage.status || message.fullMessage?.status,
          assignedTo: normalizedMessage.assignedTo || normalizedMessage.assignedToId || message.fullMessage?.assignedTo || message.fullMessage?.assignedToId,
          assignedToName: normalizedMessage.assignedToName || message.fullMessage?.assignedToName,
        }),
        lastMessage: {
          content: isProductCatalogInboxPreview ? catalogOrderPreview : ["chat", "list", "text", "template", "story_reply", "post_comment", "live_comment", "instagram_reaction", "facebook_reaction"].includes(actualType) ? (messageChat?.body || messageChat?.message || normalizedMessage.chat?.body || normalizedMessage.chat?.message || (actualType === "story_reply" ? "Reply to story" : (actualType === "post_comment" ? "Comment on post" : (actualType === "live_comment" ? "Live comment" : (actualType === "instagram_reaction" || actualType === "facebook_reaction" ? "Reacted to your message" : ""))))) : actualType,
          timestamp: getTimestampISO(messageChat?.timestamp || messageChat?.time),
          type: isProductCatalogInboxPreview ? (messageChat?.messageType === "catalog_purchase" ? "catalog_purchase" : "catalog") : actualType,
          messageType: isProductCatalogInboxPreview ? (messageChat?.messageType === "catalog_purchase" ? "catalog_purchase" : "catalog") : messageChat?.messageType || (isInternalMentionForRecent ? "text" : actualType),
          sendername: lastMessageSendername,
          performedBy: resolvedPerformedBy,
          performedByName: resolvedPerformedByName,
          ...(lastMessageIsWorkflowAutomated ? { isWorkflowAutomated: true } : {}),
          ...(isInternalMentionForRecent && { status: 'internal' })
        },
        activeStatus: normalizedMessage.hoursRemaining || normalizedMessage.hoursLeft || 0,
        isForActiveChat,
        hoursRemaining: normalizedMessage.hoursRemaining, hoursLeft: normalizedMessage.hoursLeft,
        platform: normalizedMessage.platform ?? message?.platform ?? "whatsapp",
        lastMessageFromContact: lastMessageFromContactFlag, lastMessageUnreadByTeam: lastMessageUnreadByTeamFlag,
        ...(chatInboxWs != null && chatInboxWs !== "" && { workspaceId: chatInboxWs }),
        // WhatsApp Groups plan: this payload never carried isGroup through, so addMessageCard
        // (inboxRecentSlice.js) had no way to know a live update was for a group and only ever
        // patched state.data.All, leaving the Groups tab stale until a manual reselect/refresh
        // (bug found 2026-09-25 during live group testing). message.isGroup is set server-side
        // by handleGroupInboxMessage (socketHandler.js).
        ...(normalizedMessage.isGroup === true && { isGroup: true, groupId: normalizedMessage.groupId || contactId }),
        // Multi-number: which number this message travelled through (inbox number filter guard).
        ...(normalizedMessage.connectionId && { connectionId: normalizedMessage.connectionId }),
      };

      if (isFromContact && !isForActiveChat) chatMessagePayload.receivedCount = 1;
      else chatMessagePayload.receivedCount = 0;

      if (!normalizedMessage.chat && messageChat) normalizedMessage.chat = messageChat;

      let isContactClosed = false; let shouldAllowUpdate = false;
      if (store && typeof store.getState === "function") {
        try {
          const state = store.getState(); const contactIdStr = String(contactId);
          isContactClosed = state?.inbox?.data?.Closed?.some(c => { const cId = c?.contactId || c?.contactID; return cId && String(cId) === contactIdStr; });
          if (isContactClosed) {
            const contactStatus = chatMessagePayload.status ? String(chatMessagePayload.status) : null;
            const isUnassigned = contactStatus === "Unassigned" || !chatMessagePayload.assignedTo || chatMessagePayload.assignedTo === "Unassigned" || chatMessagePayload.assignedTo === null;
            if (isUnassigned) shouldAllowUpdate = true;
          }
        } catch (_) {}
      }

      if (!isContactClosed || shouldAllowUpdate || isInternalMentionForRecent) {
        if (isInternalMentionForRecent) chatMessagePayload.isForActiveChat = false;
        debouncedAddMessageCard(chatMessagePayload);
      }

      if (dispatchFn && store && typeof store.getState === "function") {
        const state = store.getState();
        const currentContactInfo = state?.inboxInfo?.contactInfo;
        const selectedContactId = state?.inboxAll?.selectedContactID;
        const isContactBeingViewed = (currentContactInfo && currentContactInfo._id === contactId) || (selectedContactId === contactId) || resolveIsForActiveChat(contactId, normalizedMessage, message, message.fullMessage);
        if (isContactBeingViewed) {
          const serverAssignedTo = isInternalMentionForRecent ? undefined : (normalizedMessage.assignedTo || normalizedMessage.assignedToId || message.fullMessage?.assignedTo || message.fullMessage?.assignedToId || null);
          const serverAssignedToName = isInternalMentionForRecent ? undefined : (normalizedMessage.assignedToName || message.fullMessage?.assignedToName || null);
          if (!isInternalMentionForRecent && (serverAssignedTo !== undefined || serverAssignedToName !== undefined)) {
            const contactInfoPayload = {
              _id: contactId,
              status: normalizedMessage.status || message.fullMessage?.status || currentContactInfo?.status || "Assigned",
              assignedTo: serverAssignedTo !== undefined ? serverAssignedTo : (currentContactInfo?.assignedTo || null),
              assignedToName: serverAssignedToName !== undefined ? serverAssignedToName : (currentContactInfo?.assignedToName || null),
              assignedToId: serverAssignedTo !== undefined ? serverAssignedTo : (currentContactInfo?.assignedToId || null),
              name: normalizedMessage.name || normalizedMessage.contactName || currentContactInfo?.name || "Unknown",
              phoneNumber: normalizedMessage.phoneNumber || normalizedMessage.phoneNumberID || currentContactInfo?.phoneNumber,
              ...(currentContactInfo && currentContactInfo._id === contactId ? {
                labels: currentContactInfo.labels || [],
                hoursRemaining: normalizedMessage.hoursRemaining !== undefined ? normalizedMessage.hoursRemaining : currentContactInfo.hoursRemaining,
                alwaysAssignedEnabled: currentContactInfo.alwaysAssignedEnabled || false,
                alwaysAssignedTo: currentContactInfo.alwaysAssignedTo || null,
                alwaysAssignedToType: currentContactInfo.alwaysAssignedToType || null
              } : {})
            };
            dispatchFn({ type: 'inboxInfo/setContactInfo', payload: contactInfoPayload });
          }
        }
      }

      // Notifications for incoming / internal mention messages
      const isInternalMentionForNotification = messageChat?.isInternalMention || messageChat?.status === "internal" || normalizedMessage.chat?.isInternalMention || normalizedMessage.chat?.status === "internal";
      if ((isFromContact || isInternalMentionForNotification) && messageChat) {
        const messageTimestamp = messageChat.timestamp || messageChat.time || messageChat.createdAt;
        let isNewMessage = false;
        if (messageTimestamp) {
          const timeDiff = Date.now() - new Date(messageTimestamp).getTime();
          isNewMessage = timeDiff < 10000 && timeDiff >= 0;
        }
        if (!isNewMessage && (messageChat.status === "received" || messageChat.status === "internal")) isNewMessage = true;

        if (isNewMessage) {
          const state = store.getState();
          const settings = state.notifications?.settings || { soundEnabled: true, notifyOnAllIncomingMessages: true, notifyOnMessagesFromAssignedContacts: false, notifyOnAssignment: true, browserNotificationsEnabled: true, channels: [] };
          const messageStatus = messageChat.status || normalizedMessage.status;
          const isIncomingMessage = messageStatus === "received" || messageStatus === "delivered";
          const isStatusUpdate = messageChat.type === "status_update" || messageChat.type === "read_update" || normalizedMessage.type === "status_update";
          const isInternalMention = messageChat?.isInternalMention || messageChat?.status === "internal" || normalizedMessage.chat?.isInternalMention || normalizedMessage.chat?.status === "internal";

          if (isInternalMention && !isStatusUpdate) {
            const currentUser = getCachedUserData();
            const currentUserId = currentUser?._id || currentUser?.id;
            const senderId = messageChat?.senderid || normalizedMessage.chat?.senderid || message?.senderid;
            const isCurrentUserSender = currentUserId && senderId && currentUserId.toString() === senderId.toString();
            if (!isCurrentUserSender) {
              const currentPath = window.location.pathname || '';
              const isOnInboxPage = currentPath.includes('/inbox'); const isTabActive = !document.hidden;
              let isWorkspaceIgnored = false;
              const workspaceId = message.workspaceId || normalizedMessage.workspaceId;
              if (settings.channels && workspaceId) {
                const wsStr = workspaceId?.toString ? workspaceId.toString() : String(workspaceId);
                const ws = settings.channels.find(ch => { const cw = ch.workspaceID || ch._id; if (!cw) return false; return (cw.toString ? cw.toString() : String(cw)) === wsStr; });
                if (ws?.isIgnored) isWorkspaceIgnored = true;
              }
              let shouldShowNotification = false;
              if (!isWorkspaceIgnored && settings.browserNotificationsEnabled) {
                if (settings.notifyOnAllIncomingMessages) shouldShowNotification = true;
                else if (settings.notifyOnMessagesFromAssignedContacts) {
                  const userDetails = state.userDetails?.userDetails;
                  shouldShowNotification = isContactAssignedToUserOrTeam(userDetails, contactId);
                }
              }
              // Sound follows the same notification toggle — no separate sound setting.
              if (shouldPlayInboxAlertForCurrentAccount(normalizedMessage) && shouldShowNotification) playNotificationSound({}).catch(() => {});
              if (shouldPlayInboxAlertForCurrentAccount(normalizedMessage) && shouldShowNotification && (!isOnInboxPage || !isTabActive)) {
                const contactName = normalizedMessage.contactName || normalizedMessage.name || "Unknown";
                const senderName = messageChat?.sendername || normalizedMessage.chat?.sendername || "Team Member";
                const messageText = messageChat?.body || messageChat?.message || normalizedMessage.chat?.body || normalizedMessage.chat?.message || normalizedMessage.body || normalizedMessage.message || "";
                const messageBody = messageText ? `${senderName} mentioned: ${messageText}` : `${senderName} mentioned you`;
                notifyUser(contactName, messageBody, settings, contactName, contactId).catch(() => {});
              }
            }
          }

          if (isIncomingMessage && !isStatusUpdate) {
            const currentPath = window.location.pathname || '';
            const isOnInboxPage = currentPath.includes('/inbox'); const isTabActive = !document.hidden;
            let isWorkspaceIgnored = false;
            const workspaceId = message.workspaceId || normalizedMessage.workspaceId;
            if (settings.channels && workspaceId) {
              const wsStr = workspaceId?.toString ? workspaceId.toString() : String(workspaceId);
              const ws = settings.channels.find(ch => { const cw = ch.workspaceID || ch._id; if (!cw) return false; return (cw.toString ? cw.toString() : String(cw)) === wsStr; });
              if (ws?.isIgnored) isWorkspaceIgnored = true;
            }
            let shouldShowNotification = false;
            if (!isWorkspaceIgnored && settings.browserNotificationsEnabled) {
              if (settings.notifyOnAllIncomingMessages) shouldShowNotification = true;
              else if (settings.notifyOnMessagesFromAssignedContacts) {
                const userDetails = state.userDetails?.userDetails;
                shouldShowNotification = isContactAssignedToUserOrTeam(userDetails, contactId);
              }
            }
            // TEMP DEBUG — remove once the silent-notification report is resolved.
            if (isConsoleVerbose) {
            console.log('🔔 [NOTIFY DEBUG]', {
              accountMatch: shouldPlayInboxAlertForCurrentAccount(normalizedMessage),
              browserNotificationsEnabled: settings.browserNotificationsEnabled,
              notifyOnAllIncomingMessages: settings.notifyOnAllIncomingMessages,
              notifyOnMessagesFromAssignedContacts: settings.notifyOnMessagesFromAssignedContacts,
              isWorkspaceIgnored,
              shouldShowNotification,
              browserPermission: typeof Notification !== 'undefined' ? Notification.permission : 'unsupported',
            });
            }
            // Sound follows the same notification toggle — no separate sound setting.
            if (shouldPlayInboxAlertForCurrentAccount(normalizedMessage) && shouldShowNotification) playNotificationSound({}).catch(() => {});
            if (shouldPlayInboxAlertForCurrentAccount(normalizedMessage) && shouldShowNotification && (!isOnInboxPage || !isTabActive)) {
              const contactName = normalizedMessage.contactName || normalizedMessage.name || "Unknown";
              let messageBody = "New message";
              const mtype = messageChat.messageType || messageChat.type;
              if (mtype === "catalog_purchase") messageBody = "🛒 Product Order";
              else if (mtype === "image") messageBody = "Photo";
              else if (mtype === "video") messageBody = "Video";
              else if (mtype === "audio") messageBody = "Audio";
              else if (mtype === "document") messageBody = "Document";
              else if (mtype === "list") messageBody = "List Message";
              else if (mtype === "template") messageBody = "Template Message";
              else if (mtype === "catalog") messageBody = "🛒 Product Order";
              else if (messageChat.body) {
                let isJson = false;
                if (typeof messageChat.body === 'string' && messageChat.body.trim().startsWith('{')) { try { JSON.parse(messageChat.body); isJson = true; } catch (_) {} }
                if (!isJson) { const t = messageChat.body; messageBody = t.length > 50 ? t.substring(0, 50) + "..." : t; }
              } else if (messageChat.message) {
                let isJson = false;
                if (typeof messageChat.message === 'string' && messageChat.message.trim().startsWith('{')) { try { JSON.parse(messageChat.message); isJson = true; } catch (_) {} }
                if (!isJson) { const t = messageChat.message; messageBody = t.length > 50 ? t.substring(0, 50) + "..." : t; }
              }
              notifyUser(contactName, messageBody, settings, contactName, contactId).catch(() => {});
            }
          }
        }
      }

      // Add to active chat
      if (isForActiveChat) {
        if (!normalizedMessage.chat) {
          if (normalizedMessage.message && typeof normalizedMessage.message === 'object') normalizedMessage.chat = normalizedMessage.message;
          else if (messageChat) normalizedMessage.chat = messageChat;
          else if (message.fullMessage?.chat) normalizedMessage.chat = { ...message.fullMessage.chat };
          else return;
        }
        const transformedMessage = transformSocketMessage(normalizedMessage);
        if (!transformedMessage.chat) {
          if (normalizedMessage.chat) transformedMessage.chat = normalizedMessage.chat;
          else if (messageChat) transformedMessage.chat = messageChat;
          else return;
        }

        // Preserve reply + media fields
        const replySenderNameSource = normalizedMessage.chat?.replySenderName || messageChat?.replySenderName || normalizedMessage.replySenderName || message.message?.replySenderName || message.chat?.replySenderName;
        if (replySenderNameSource !== undefined && replySenderNameSource !== null) transformedMessage.chat.replySenderName = replySenderNameSource;
        const replyBodySource = normalizedMessage.chat?.replyBody || messageChat?.replyBody || normalizedMessage.replyBody || message.message?.replyBody;
        if (replyBodySource !== undefined) transformedMessage.chat.replyBody = replyBodySource;
        const replyTypeSource = normalizedMessage.chat?.replyType || messageChat?.replyType || normalizedMessage.replyType || message.message?.replyType;
        if (replyTypeSource !== undefined) transformedMessage.chat.replyType = replyTypeSource;
        const replyIdSource = normalizedMessage.chat?.replyId || messageChat?.replyId || normalizedMessage.replyId || message.message?.replyId || normalizedMessage.chat?.replyTo || messageChat?.replyTo || message.message?.replyTo;
        if (replyIdSource !== undefined) transformedMessage.chat.replyId = replyIdSource;
        const replyToSource = normalizedMessage.chat?.replyTo || messageChat?.replyTo || normalizedMessage.replyTo || message.message?.replyTo;
        if (replyToSource !== undefined) { transformedMessage.chat.replyTo = replyToSource; if (!transformedMessage.chat.replyId) transformedMessage.chat.replyId = transformedMessage.chat.replyTo; }

        if (transformedMessage.chat?.type === "story_reply" || normalizedMessage.chat?.type === "story_reply") {
          const storyUrlSrc = normalizedMessage.chat?.storyUrl ?? messageChat?.storyUrl ?? message.fullMessage?.chat?.storyUrl;
          const storyIdSrc = normalizedMessage.chat?.storyId ?? messageChat?.storyId ?? message.fullMessage?.chat?.storyId;
          const storyAvatarSrc = normalizedMessage.chat?.storyReplyAvatarUrl ?? messageChat?.storyReplyAvatarUrl ?? message.fullMessage?.chat?.storyReplyAvatarUrl;
          if (storyUrlSrc !== undefined) transformedMessage.chat.storyUrl = storyUrlSrc;
          if (storyIdSrc !== undefined) transformedMessage.chat.storyId = storyIdSrc;
          if (storyAvatarSrc !== undefined) transformedMessage.chat.storyReplyAvatarUrl = storyAvatarSrc;
        }
        if (transformedMessage.chat?.type === "post_comment" || normalizedMessage.chat?.type === "post_comment") {
          const postMediaUrlSrc = normalizedMessage.chat?.postMediaUrl ?? messageChat?.postMediaUrl ?? message.fullMessage?.chat?.postMediaUrl;
          const postPermalinkSrc = normalizedMessage.chat?.postPermalink ?? messageChat?.postPermalink ?? message.fullMessage?.chat?.postPermalink;
          const postMediaTypeSrc = normalizedMessage.chat?.postMediaType ?? messageChat?.postMediaType ?? message.fullMessage?.chat?.postMediaType;
          if (postMediaUrlSrc !== undefined) transformedMessage.chat.postMediaUrl = postMediaUrlSrc;
          if (postPermalinkSrc !== undefined) transformedMessage.chat.postPermalink = postPermalinkSrc;
          if (postMediaTypeSrc !== undefined) transformedMessage.chat.postMediaType = postMediaTypeSrc;
        }
        if (transformedMessage.chat?.type === "instagram_reaction" || normalizedMessage.chat?.type === "instagram_reaction") {
          const reactionSrc = normalizedMessage.chat?.reaction ?? messageChat?.reaction ?? message.fullMessage?.chat?.reaction;
          const reactionPreviewSrc = normalizedMessage.chat?.reactionPreview ?? messageChat?.reactionPreview ?? message.fullMessage?.chat?.reactionPreview;
          if (reactionSrc !== undefined) transformedMessage.chat.reaction = reactionSrc;
          if (reactionPreviewSrc !== undefined) transformedMessage.chat.reactionPreview = reactionPreviewSrc;
        }
        if (transformedMessage.chat?.type === "facebook_reaction" || normalizedMessage.chat?.type === "facebook_reaction") {
          const reactionSrc = normalizedMessage.chat?.reaction ?? messageChat?.reaction ?? message.fullMessage?.chat?.reaction;
          const reactionPreviewSrc = normalizedMessage.chat?.reactionPreview ?? messageChat?.reactionPreview ?? message.fullMessage?.chat?.reactionPreview;
          if (reactionSrc !== undefined) transformedMessage.chat.reaction = reactionSrc;
          if (reactionPreviewSrc !== undefined) transformedMessage.chat.reactionPreview = reactionPreviewSrc;
        }
        if (normalizedMessage.chat?.reaction !== undefined || messageChat?.reaction !== undefined) {
          transformedMessage.chat.reaction = normalizedMessage.chat?.reaction ?? messageChat?.reaction ?? message.fullMessage?.chat?.reaction;
        }

        // Ensure timestamps are ISO strings
        if (!transformedMessage.chat.createdAt) { const ts = transformedMessage.chat.timestamp || transformedMessage.chat.time; transformedMessage.chat.createdAt = ts ? (typeof ts === 'string' ? ts : new Date(ts).toISOString()) : new Date().toISOString(); }
        else if (transformedMessage.chat.createdAt instanceof Date) transformedMessage.chat.createdAt = transformedMessage.chat.createdAt.toISOString();
        else if (typeof transformedMessage.chat.createdAt !== 'string') transformedMessage.chat.createdAt = new Date(transformedMessage.chat.createdAt).toISOString();
        if (transformedMessage.chat.timestamp instanceof Date) transformedMessage.chat.timestamp = transformedMessage.chat.timestamp.toISOString();
        else if (transformedMessage.chat.timestamp && typeof transformedMessage.chat.timestamp !== 'string') transformedMessage.chat.timestamp = new Date(transformedMessage.chat.timestamp).toISOString();
        if (transformedMessage.chat.time) {
          if (typeof transformedMessage.chat.time === 'string' && (transformedMessage.chat.time.includes('T') || transformedMessage.chat.time.match(/^\d{4}-\d{2}-\d{2}/))) { /* already ISO */ }
          else if (transformedMessage.chat.time instanceof Date) transformedMessage.chat.time = transformedMessage.chat.time.toISOString();
          else if (typeof transformedMessage.chat.time !== 'string') transformedMessage.chat.time = new Date(transformedMessage.chat.time).toISOString();
          else if (!transformedMessage.chat.time.includes('AM') && !transformedMessage.chat.time.includes('PM') && !transformedMessage.chat.time.includes('Yesterday')) { const pd = new Date(transformedMessage.chat.time); if (!isNaN(pd.getTime())) transformedMessage.chat.time = pd.toISOString(); }
        }

        mergeResolvedSenderFieldsOntoChat(transformedMessage.chat, normalizedMessage, message);
        const isInternalMention = transformedMessage.chat?.isInternalMention || transformedMessage.chat?.status === "internal";
        const inboxPlatformTransform = transformedMessage.platform ?? normalizedMessage?.platform ?? "whatsapp";
        const isWorkflowMessage = !transformedMessage.chat.senderid && !transformedMessage.chat.clientid && !transformedMessage.chat.sendername && !transformedMessage.chat.clientname && !isInternalMention;
        // Only apply legacy heuristic when performedBy is absent (old messages without the field)
        if (isWorkflowMessage && !transformedMessage.chat.clientid && !transformedMessage.chat.clientname && !transformedMessage.chat.sendername && !isInternalMention && !transformedMessage.chat.performedBy) {
          applyWorkflowAutomatedMetadata(transformedMessage.chat, inboxPlatformTransform, true, isInternalMention);
        }

        if (isInternalMention) {
          let sendernameSource = transformedMessage.chat.sendername || normalizedMessage?.chat?.sendername || normalizedMessage?.message?.sendername || message?.chat?.sendername || message?.message?.sendername;
          if (!sendernameSource && transformedMessage.chat.senderid) { const sm = message?.message || message?.chat; if (sm?.sendername) sendernameSource = sm.sendername; }
          const senderAvatarColorSource = transformedMessage.chat.senderAvatarColor || normalizedMessage?.chat?.senderAvatarColor || normalizedMessage?.message?.senderAvatarColor || message?.chat?.senderAvatarColor || message?.message?.senderAvatarColor;
          if (sendernameSource && !transformedMessage.chat.sendername) transformedMessage.chat.sendername = sendernameSource;
          if (senderAvatarColorSource && !transformedMessage.chat.senderAvatarColor) transformedMessage.chat.senderAvatarColor = senderAvatarColorSource;
          transformedMessage.chat.isInternalMention = true;
        }

        if (transformedMessage.chat.clientid && (transformedMessage.chat.clientname === null || transformedMessage.chat.clientname === undefined)) {
          if (normalizedMessage.name) transformedMessage.chat.clientname = normalizedMessage.name;
          else if (normalizedMessage.contactName) transformedMessage.chat.clientname = normalizedMessage.contactName;
          else if (message.fullMessage?.name) transformedMessage.chat.clientname = message.fullMessage.name;
        }

        sanitizeOutboundChatDirection(transformedMessage.chat);

        if (!transformedMessage.chat.sendername && !transformedMessage.chat.clientname) {
          if (normalizedMessage.assignedToName && normalizedMessage.assignedToName !== "Unknown") transformedMessage.chat.sendername = normalizedMessage.assignedToName;
          else if (message.assignedToName && message.assignedToName !== "Unknown") transformedMessage.chat.sendername = message.assignedToName;
        }

        if (!transformedMessage.chat.messageType && transformedMessage.chat.type) transformedMessage.chat.messageType = transformedMessage.chat.type;

        if (transformedMessage.chat && (transformedMessage.chat.messageType === "list" || transformedMessage.chat.messageType === "template" || transformedMessage.chat.messageType === "catalog" || transformedMessage.chat.messageType === "catalog_purchase" || transformedMessage.chat.type === "list" || transformedMessage.chat.type === "template" || transformedMessage.chat.type === "catalog" || transformedMessage.chat.type === "catalog_purchase")) {
          if (!transformedMessage.chat.body && transformedMessage.chat.message) transformedMessage.chat.body = transformedMessage.chat.message;
          if (!transformedMessage.chat.message && transformedMessage.chat.body) transformedMessage.chat.message = transformedMessage.chat.body;
          if (transformedMessage.chat.type === "list" || transformedMessage.chat.messageType === "list" || normalizedMessage.chatType === "list") { transformedMessage.chat.messageType = "list"; transformedMessage.chat.type = "chat"; }
          else if (!transformedMessage.chat.messageType) transformedMessage.chat.messageType = transformedMessage.chat.type;
          if (normalizedMessage.chatType && normalizedMessage.chatType !== "list") {
            transformedMessage.chat.messageType = normalizedMessage.chatType;
            if (normalizedMessage.chatType === "template" || normalizedMessage.chatType === "catalog") transformedMessage.chat.type = normalizedMessage.chatType;
            else if (normalizedMessage.chatType === "catalog_purchase") transformedMessage.chat.type = "chat";
          }
          const mcd = transformedMessage.chat?.body || transformedMessage.chat?.message || "";
          let isCPD = transformedMessage.chat?.type === "catalog_purchase" || transformedMessage.chat?.messageType === "catalog_purchase";
          if (!isCPD && mcd && typeof mcd === 'string' && mcd.trim()) { try { const pb = JSON.parse(mcd); if (pb && typeof pb === 'object' && (pb.productId || pb.catalogId || pb.allProducts || pb.items)) isCPD = true; } catch (_) {} }
          if (isCPD) { transformedMessage.chat.messageType = "catalog_purchase"; transformedMessage.chat.type = "chat"; }
        }

        if (transformedMessage.chat) {
          if (!transformedMessage.chat.message || !transformedMessage.chat.message.trim()) { if (transformedMessage.chat.body && transformedMessage.chat.body.trim()) transformedMessage.chat.message = transformedMessage.chat.body; }
          if (!transformedMessage.chat.body || !transformedMessage.chat.body.trim()) { if (transformedMessage.chat.message && transformedMessage.chat.message.trim()) transformedMessage.chat.body = transformedMessage.chat.message; }
          if (!transformedMessage.chat.messageType || transformedMessage.chat.messageType !== "catalog_purchase") {
            const mc = transformedMessage.chat.body || transformedMessage.chat.message || "";
            if (mc && typeof mc === 'string' && mc.trim()) { try { const pb = JSON.parse(mc); if (pb && typeof pb === 'object' && (pb.productId || pb.catalogId || pb.allProducts || pb.items)) { transformedMessage.chat.messageType = "catalog_purchase"; transformedMessage.chat.type = "chat"; } } catch (_) {} }
          }
          if (transformedMessage.chat.messageType && ["image", "video", "audio", "document", "location", "contacts", "text", "catalog_purchase"].includes(transformedMessage.chat.messageType)) transformedMessage.chat.type = "chat";
        }

        const messageForRedux = { ...transformedMessage, isForActiveChat: true };
        if (!dispatchFn || typeof dispatchFn !== 'function') return;

        // Outbound team sends are applied to the open chat via receive_message (or optimistic pending + API).
        // Adding them again from new_inbox_message duplicated bubbles and, when clientname was wrong, placed them on the left.
        const isOutboundTeamChatMessage =
          !isInternalMention &&
          transformedMessage.chat?.senderid &&
          !transformedMessage.chat?.clientid;
        if (!isOutboundTeamChatMessage) {
          dispatchFn(addMessage(messageForRedux));
        }

        // Contact info update
        const state = store?.getState ? store.getState() : null;
        const currentContactInfo = state?.inboxInfo?.contactInfo;
        const isAssignmentMessage = transformedMessage.chat && transformedMessage.chat.type === "assignment";
        const isFromTeamMember = transformedMessage.chat?.senderid && transformedMessage.chat?.sendername && !transformedMessage.chat?.clientid && !transformedMessage.chat?.clientname;
        const isInternalMentionForRecent2 = transformedMessage.chat?.isInternalMention || transformedMessage.chat?.status === "internal" || transformedMessage.isInternalMention || transformedMessage.status === "internal" || normalizedMessage.chat?.isInternalMention || normalizedMessage.chat?.status === "internal";
        let assignmentDataOther = {};
        if (isAssignmentMessage) {
          let assignedToName = transformedMessage.assignedToName || transformedMessage.chat?.toName || transformedMessage.teamName;
          const assignedToId = transformedMessage.assignedTo || transformedMessage.assignedToId || transformedMessage.chat?.to;
          if (!assignedToName || assignedToName === assignedToId) assignedToName = transformedMessage.teamName || transformedMessage.chat?.teamName || normalizedMessage?.teamName || message?.teamName || message?.team?.name || assignedToName;
          assignmentDataOther = { ...(assignedToId !== undefined && { assignedTo: assignedToId }), ...(assignedToName !== undefined && { assignedToName }), ...(assignedToId !== undefined && { assignedToId }), ...(transformedMessage.status !== undefined && { status: transformedMessage.status }) };
          if (currentContactInfo && currentContactInfo._id === contactId) {
            assignmentDataOther = { ...(assignmentDataOther.assignedTo === undefined && currentContactInfo.assignedTo !== undefined && { assignedTo: currentContactInfo.assignedTo }), ...(assignmentDataOther.assignedToName === undefined && currentContactInfo.assignedToName !== undefined && { assignedToName: currentContactInfo.assignedToName }), ...(assignmentDataOther.assignedToId === undefined && currentContactInfo.assignedToId !== undefined && { assignedToId: currentContactInfo.assignedToId }), ...(assignmentDataOther.status === undefined && currentContactInfo.status !== undefined && { status: currentContactInfo.status }), ...assignmentDataOther };
          }
        } else if (isFromTeamMember && !isInternalMentionForRecent2) {
          const hasAlwaysAssigned = currentContactInfo && currentContactInfo.alwaysAssignedEnabled;
          const serverAssignedTo = transformedMessage.assignedTo || transformedMessage.assignedToId || normalizedMessage.assignedTo || normalizedMessage.assignedToId || message?.assignedTo || message?.assignedToId;
          const serverAssignedToName = transformedMessage.assignedToName || normalizedMessage.assignedToName || message?.assignedToName;
          if (hasAlwaysAssigned && serverAssignedTo) { assignmentDataOther = { assignedTo: serverAssignedTo, assignedToName: serverAssignedToName || currentContactInfo?.assignedToName, assignedToId: serverAssignedTo, status: "Assigned" }; }
          else if (hasAlwaysAssigned) { assignmentDataOther = { assignedTo: currentContactInfo?.assignedTo ?? null, assignedToName: currentContactInfo?.assignedToName ?? null, assignedToId: currentContactInfo?.assignedToId ?? currentContactInfo?.assignedTo ?? null, status: currentContactInfo?.status || "Assigned" }; }
          else {
            const senderName = transformedMessage.chat.sendername; const senderId = transformedMessage.chat.senderid;
            const isTeamId = senderName === senderId;
            const teamName = transformedMessage.teamName || transformedMessage.chat?.teamName || normalizedMessage?.teamName || message?.teamName || message?.team?.name;
            assignmentDataOther = { assignedTo: senderId, assignedToName: isTeamId ? (teamName || senderId) : senderName, assignedToId: senderId, status: "Assigned" };
          }
        } else if (currentContactInfo && currentContactInfo._id === contactId) {
          assignmentDataOther = { ...(currentContactInfo.assignedTo !== undefined && { assignedTo: currentContactInfo.assignedTo }), ...(currentContactInfo.assignedToName !== undefined && { assignedToName: currentContactInfo.assignedToName }), ...(currentContactInfo.assignedToId !== undefined && { assignedToId: currentContactInfo.assignedToId }), ...(currentContactInfo.status !== undefined && { status: currentContactInfo.status }) };
        }

        const contactInfoPayload = {
          _id: contactId,
          name: transformedMessage.contactName || normalizedMessage.name || "Unknown",
          phoneNumber: transformedMessage.contactNumber || normalizedMessage.phoneNumber || normalizedMessage.chat?.from,
          ...getCommentReplyPatch(transformedMessage.chat, transformedMessage.chat?.clientid != null && String(transformedMessage.chat.clientid) === String(contactId), isInternalMentionForRecent2),
          ...(!isInternalMentionForRecent2 && assignmentDataOther),
          ...(!isInternalMentionForRecent2 && transformedMessage.status !== undefined && { status: transformedMessage.status }),
          ...(transformedMessage.hoursRemaining !== undefined && { hoursRemaining: transformedMessage.hoursRemaining }),
          ...(transformedMessage.labels !== undefined && { labels: transformedMessage.labels })
        };

        if (dispatchFn && typeof dispatchFn === 'function') {
          const contactName = transformedMessage.contactName || normalizedMessage?.contactName || normalizedMessage?.name || message?.fullMessage?.name || message?.name || contactInfoPayload.name;
          batch(() => {
            if (!isInternalMentionForRecent2) dispatchFn({ type: 'inboxInfo/setContactInfo', payload: contactInfoPayload });
            if (contactName) dispatchFn(updateContactName({ contactId, name: contactName }));
          });
        }

        if (!isInternalMentionForRecent2 && (isAssignmentMessage || isFromTeamMember || transformedMessage.assignedTo !== undefined || transformedMessage.assignedToName !== undefined || transformedMessage.status !== undefined) && currentContactInfo && currentContactInfo._id === contactId) {
          const assignmentEventData = {
            contactId, contactID: contactId,
            status: assignmentDataOther.status || transformedMessage.status || currentContactInfo.status,
            assignedTo: assignmentDataOther.assignedTo !== undefined ? assignmentDataOther.assignedTo : (transformedMessage.assignedTo !== undefined ? transformedMessage.assignedTo : (currentContactInfo.assignedTo || null)),
            assignedToName: assignmentDataOther.assignedToName !== undefined ? assignmentDataOther.assignedToName : (transformedMessage.assignedToName !== undefined ? transformedMessage.assignedToName : (currentContactInfo.assignedToName || null)),
            assignedToId: assignmentDataOther.assignedToId !== undefined ? assignmentDataOther.assignedToId : (transformedMessage.assignedToId !== undefined ? transformedMessage.assignedToId : (currentContactInfo.assignedToId || null)),
          };
          window.dispatchEvent(new CustomEvent('assignmentUpdate', { detail: assignmentEventData }));
          window.dispatchEvent(new CustomEvent('contactStatusUpdate', { detail: assignmentEventData }));
        }
      }
    }
  } catch (_) {}
};

// Content-free assignment-count delta, broadcast to anyone permitted to view the
// Team/Team Member lists (see server/utils/socketHandler.js emitAssignmentCountChanged).
// Unlike new_inbox_message, this is NOT gated by per-conversation view permissions,
// so InboxSettings sidebar counts stay live even for viewers who can't see the
// conversation itself (e.g. an admin watching a workflow assign to someone else's team).
export const handleAssignmentCountChanged = (data) => {
  if (!ensureDispatchReady()) { queueEvent('assignment_count_changed', data, handleAssignmentCountChanged); return; }
  const dispatchFn = getDispatch();
  try {
    const { contactId, oldAssignedTo, oldAssignedToType, newAssignedTo, newAssignedToType } = data || {};
    if (!contactId || !dispatchFn) return;

    // 'agent' (AI Takeover — see enableTakeover/disableTakeover) has its own counter
    // in aiAgentsSlice, not inboxSettings.teamMembers/teams — must be checked before
    // the team/teammember ternaries below, which otherwise default anything that
    // isn't 'team' straight to 'teammember' and would corrupt a real team member's
    // count with the agent's id (Rahim, 2026-09-02: "count not increase... on refresh"
    // led here while tracing the live-update side of the same feature).
    if (oldAssignedToType === 'agent' && oldAssignedTo && String(oldAssignedTo) !== String(newAssignedTo)) {
      dispatchFn(adjustAgentCount({ agentId: oldAssignedTo, delta: -1 }));
    } else if (oldAssignedTo && oldAssignedToType && String(oldAssignedTo) !== String(newAssignedTo)) {
      dispatchFn(removeContactFromAssignment({
        contactId,
        assignedToId: oldAssignedTo,
        assignedToType: oldAssignedToType === 'team' ? 'team' : 'teammember',
      }));
    }
    if (newAssignedToType === 'agent' && newAssignedTo) {
      dispatchFn(adjustAgentCount({ agentId: newAssignedTo, delta: 1 }));
    } else if (newAssignedTo && newAssignedToType) {
      dispatchFn(addContactToAssignment({
        contactId,
        assignedToId: newAssignedTo,
        assignedToType: newAssignedToType === 'team' ? 'team' : 'teammember',
      }));
    }
  } catch (error) {
    console.error('Error handling assignment_count_changed:', error);
  }
};

// ─── presence handlers ────────────────────────────────────────────────────────
export const handleUserConnectedUpdate = (userData) => {
  if (!ensureDispatchReady()) { queueEvent('user_connected_update', userData, handleUserConnectedUpdate); return; }
  const dispatchFn = getDispatch();
  try {
    if (dispatchFn && typeof dispatchFn === 'function') {
      dispatchFn(addActiveMember(userData));
      const currentUser = getCachedUserData();
      const currentUserId = currentUser?._id || currentUser?.id;
      const connectedUserId = userData?._id || userData?.id;
      if (currentUserId && connectedUserId && currentUserId.toString() === connectedUserId.toString()) {
        dispatchFn(fetchTeamMemberChats());
        console.log('🔄 Refreshed team member chats after current user came online');
      }
    }
  } catch (_) {}
};

export const handleUserDisconnectedUpdate = (userData) => {
  if (!ensureDispatchReady()) { queueEvent('user_disconnected_update', userData, handleUserDisconnectedUpdate); return; }
  const dispatchFn = getDispatch();
  try { if (dispatchFn && typeof dispatchFn === 'function') dispatchFn(removeActiveMember(userData._id)); } catch (_) {}
};

export const handleContactLabelsUpdated = (contactData) => {
  const { assignedTo, ...restOfContactData } = contactData;
  const labelCardWs = inboxCardWorkspaceIdFrom(contactData);
  const transformedData = {
    ...restOfContactData,
    hoursLeft: contactData.hoursLeft || 0,
    activeStatus: contactData.hoursRemaining || contactData.hoursLeft || 0,
    receivedCount: contactData.receivedCount || 0,
    labels: contactData.labels || [],
    ...(labelCardWs != null && labelCardWs !== "" && { workspaceId: labelCardWs }),
    ...inboxCardConnectionFields(contactData),
  };
  debouncedAddMessageCard(transformedData);
};

const EVICTION_TOAST_ID = "session-evicted";

// Renders a react-hot-toast toast.custom(...) card, bypassing the shared
// <ToastBar> render override in App.js (which 199+ other toast.error(...)
// calls in this app depend on) so this unusual "stays until manually
// dismissed" toast can't affect any of them.
const renderEvictionToast = (t, reasonText) =>
  React.createElement(
    "div",
    {
      style: {
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        background: APP_TOAST_BACKGROUND,
        color: APP_TOAST_ERROR_COLOR,
        border: "1px solid rgba(211, 47, 47, 0.28)",
        fontSize: APP_TOAST_FONT_SIZE,
        fontWeight: 500,
        lineHeight: 1.45,
        padding: "12px 16px",
        borderRadius: 8,
        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)",
        maxWidth: "min(420px, calc(100vw - 32px))",
        opacity: t.visible ? 1 : 0,
        transition: "opacity 0.2s ease",
      },
    },
    React.createElement("span", { style: { flex: 1 } }, `You have been logged out. ${reasonText}`),
    React.createElement(
      "button",
      {
        type: "button",
        onClick: () => toast.dismiss(t.id),
        "aria-label": "Dismiss",
        style: {
          background: "none",
          border: "none",
          cursor: "pointer",
          color: APP_TOAST_ERROR_COLOR,
          fontSize: 18,
          lineHeight: 1,
          padding: 0,
          flexShrink: 0,
        },
      },
      "×"
    )
  );

export const handleForcedDisconnect = (data) => {
  const type = data?.type;

  if (type === "evicted") {
    // Signed in elsewhere: stop this tab from silently re-authenticating
    // itself via the still-valid refresh cookie (tokenRefreshService.js and
    // networkErrorHandler.js both check this flag) and show a persistent
    // toast instead of the old alert()/Notification.
    clearAccessToken();
    setEvicted(true);
    toast.custom((t) => renderEvictionToast(t, data.reason), {
      id: EVICTION_TOAST_ID,
      duration: Infinity,
      position: "top-right",
    });
    // After a short readable pause, finish the job client-side: clear the
    // cached user object (so the app doesn't think it's still logged in on
    // next load) and navigate to the login page. Deliberately does NOT call
    // the logout API — the refresh-token cookie is left alone/still valid,
    // this is purely a local "stop pretending to be logged in" cleanup, same
    // as the reload-based re-auth path already relies on elsewhere.
    setTimeout(() => {
      localStorage.removeItem('user');
      window.location.href = '/';
    }, 10000);
  } else {
    // workspace_error / unknown — unchanged fallback behavior.
    if (window.Notification && Notification.permission === "granted") {
      new Notification("Connection Closed", { body: data.reason });
    } else {
      alert(`Connection closed: ${data.reason}`);
    }
  }

  // Close the server-side socket immediately (emitting alone leaves it open
  // until the client reacts) — same as before for the evicted/unknown cases.
  // Tagging this as a client-initiated disconnect also ensures no auto-
  // reconnect is attempted, which is correct here (unlike the idle case
  // above): reconnecting after eviction would just race the session that
  // replaced this one.
  if (socketState.socket) { socketState.socket.disconnect(); socketState.socket = null; }
};

export const handleConnectionWarning = (data) => {
  if (window.Notification && Notification.permission === "granted") {
    new Notification("Connection Warning", { body: data.message });
  }
};

// ─── scheduled message handlers ───────────────────────────────────────────────
export const handleScheduledMessageCreated = (data) => {
  if (!ensureDispatchReady()) { queueEvent('scheduledMessageCreated', data, handleScheduledMessageCreated); return; }
  try {
    const dispatchFn = getDispatch();
    if (data.success && data.scheduledMessage) { dispatchFn(addScheduledMessage(data.scheduledMessage)); toast.success('Message scheduled successfully'); }
  } catch (error) { console.error('Error handling scheduledMessageCreated:', error); }
};

export const handleScheduledMessageSent = (data) => {
  if (!ensureDispatchReady()) { queueEvent('scheduledMessageSent', data, handleScheduledMessageSent); return; }
  try {
    const dispatchFn = getDispatch();
    if (data.scheduledMessageId) { dispatchFn(removeScheduledMessage(data.scheduledMessageId)); toast.success('Scheduled message sent successfully'); }
  } catch (error) { console.error('Error handling scheduledMessageSent:', error); }
};

export const handleScheduledMessageFailed = (data) => {
  if (!ensureDispatchReady()) { queueEvent('scheduledMessageFailed', data, handleScheduledMessageFailed); return; }
  try {
    const dispatchFn = getDispatch();
    if (data.scheduledMessageId) { dispatchFn(updateScheduledMessage({ _id: data.scheduledMessageId, status: 'failed', errorMessage: data.reason })); toast.error(`Scheduled message failed: ${data.reason || 'Unknown error'}`); }
  } catch (error) { console.error('Error handling scheduledMessageFailed:', error); }
};

export const handleScheduledMessageCancelled = (data) => {
  if (!ensureDispatchReady()) { queueEvent('scheduledMessageCancelled', data, handleScheduledMessageCancelled); return; }
  try {
    const dispatchFn = getDispatch();
    if (data.success && data.scheduledMessageId) { dispatchFn(removeScheduledMessage(data.scheduledMessageId)); toast.success('Scheduled message cancelled'); }
  } catch (error) { console.error('Error handling scheduledMessageCancelled:', error); }
};

// ─── team member chat handlers ────────────────────────────────────────────────
export const handleTeamMemberChatMessage = (data) => {
  if (!ensureDispatchReady()) { queueEvent('team_member:chat:message', data, handleTeamMemberChatMessage); return; }
  try {
    const dispatchFn = getDispatch();
    if (!dispatchFn) return;
    if (shouldIgnoreSocketPayloadWrongAccount({ AccountID: data.accountId, accountId: data.accountId })) return;
    const { message, otherMemberId, senderId, workspaceId } = data;
    if (!message || !otherMemberId) return;
    const currentUser = getCachedUserData();
    const currentUserId = currentUser?._id || currentUser?.id;
    const isFromMe = senderId && currentUserId && senderId.toString() === currentUserId.toString();
    const messageWithFromMe = { ...message, fromMe: isFromMe, senderId: message.senderId || senderId, senderName: message.senderName || "Team Member", id: message.id || message.tempId, tempId: message.tempId || message.id, timestamp: message.timestamp || new Date().toISOString(), message: message.message || message.body || "" };
    dispatchFn(addTeamMemberMessage({ message: messageWithFromMe, otherMemberId }));
    if (!isFromMe) {
      const state = store.getState();
      const settings = state.notifications?.settings || { browserNotificationsEnabled: true, soundEnabled: true };
      const tmAccountPayload = { AccountID: data.accountId, accountId: data.accountId };
      const currentPath = window.location.pathname || '';
      const isOnInboxPage = currentPath.includes('/inbox'); const isTabActive = !document.hidden;
      let isWorkspaceIgnored = false;
      const workspaceIdToCheck = workspaceId || state.auth?.user?.workspaceId || state.workspace?.currentWorkspace?._id;
      if (settings.channels && workspaceIdToCheck) {
        const wsStr = workspaceIdToCheck?.toString ? workspaceIdToCheck.toString() : String(workspaceIdToCheck);
        const ws = settings.channels.find(ch => { const cw = ch.workspaceID || ch._id; if (!cw) return false; return (cw.toString ? cw.toString() : String(cw)) === wsStr; });
        if (ws?.isIgnored) isWorkspaceIgnored = true;
      }
      let shouldShowNotification = !isWorkspaceIgnored && settings.browserNotificationsEnabled;
      // Sound follows the same notification toggle — no separate sound setting.
      if (shouldPlayInboxAlertForCurrentAccount(tmAccountPayload) && shouldShowNotification) playNotificationSound({}).catch(() => {});
      if (shouldPlayInboxAlertForCurrentAccount(tmAccountPayload) && shouldShowNotification && (!isOnInboxPage || !isTabActive)) {
        const senderName = message.senderName || "Team Member";
        let messageBody = "New message";
        const mtype = message.messageType || "text";
        if (mtype === "image") messageBody = "Photo"; else if (mtype === "video") messageBody = "Video"; else if (mtype === "audio") messageBody = "Audio"; else if (mtype === "document") messageBody = "Document"; else if (mtype === "list") messageBody = "List Message"; else if (mtype === "template") messageBody = "Template Message"; else if (mtype === "catalog") messageBody = "Catalog Message";
        else if (message.message) {
          let isJson = false;
          if (typeof message.message === 'string' && message.message.trim().startsWith('{')) { try { JSON.parse(message.message); isJson = true; } catch (_) {} }
          if (!isJson) { const t = message.message; messageBody = t.length > 50 ? t.substring(0, 50) + "..." : t; }
        }
        notifyUser(senderName, messageBody, settings, senderName, otherMemberId).catch(() => {});
      }
    }
  } catch (error) { console.error("❌ Error in handleTeamMemberChatMessage:", error); }
};

export const handleTeamMemberChatStatusUpdate = (data) => {
  if (!ensureDispatchReady()) { queueEvent('team_member:chat:status_update', data, handleTeamMemberChatStatusUpdate); return; }
  try {
    const dispatchFn = getDispatch();
    if (!dispatchFn) return;
    const { otherMemberId, messageId, status, unreadCount } = data;
    if (!otherMemberId) return;
    batch(() => {
      if (unreadCount !== undefined) dispatchFn(updateInboxChat({ otherMemberId, unreadCount }));
      if (messageId && status) dispatchFn(updateTeamMemberMessageStatus({ messageId, status }));
    });
  } catch (error) { console.error("❌ Error in handleTeamMemberChatStatusUpdate:", error); }
};

export const handleTeamMemberChatInboxUpdate = (data) => {
  if (!ensureDispatchReady()) { queueEvent('team_member:chat:inbox_update', data, handleTeamMemberChatInboxUpdate); return; }
  try {
    const dispatchFn = getDispatch();
    if (!dispatchFn) return;
    const { otherMemberId, lastMessage, unreadCount } = data;
    if (!otherMemberId) return;
    dispatchFn(updateInboxChat({ otherMemberId, lastMessage, unreadCount }));
  } catch (error) { console.error("❌ Error in handleTeamMemberChatInboxUpdate:", error); }
};

// ─── task chat handlers ───────────────────────────────────────────────────────
export const handleTaskChatMessage = (payload) => {
  if (!ensureDispatchReady()) { queueEvent("task_chat:message", payload, handleTaskChatMessage); return; }
  try {
    const dispatchFn = getDispatch();
    if (!dispatchFn || !payload?.taskId || !payload?.envelope) return;
    const state = store.getState();
    const viewerId = state.auth?.user?._id || state.auth?.user?.id || state.userDetails?.userDetails?._id || state.userDetails?.userDetails?.id;
    batch(() => {
      dispatchFn(taskChatSocketEnvelope({ viewerId, taskId: payload.taskId, envelope: payload.envelope }));
      dispatchFn(taskChatListSocketUpdate({ taskId: payload.taskId, envelope: payload.envelope, viewerId }));
    });
    const env = payload.envelope;
    const fromOther = env?.senderId && viewerId && String(env.senderId) !== String(viewerId);
    if (fromOther) {
      const settings = state.notifications?.settings || { browserNotificationsEnabled: true };
      // Sound follows the same notification toggle — no separate sound setting.
      if (settings.browserNotificationsEnabled) {
        try { playNotificationSound(); } catch (_) {}
      }
      const isUserChat = env?.kind === "user";
      if (isUserChat) {
        if (settings.browserNotificationsEnabled) {
          const senderName = env.senderName || "Member";
          const taskName = state.taskChat?.chatList?.items?.find((i) => i.taskId === payload.taskId)?.taskName || null;
          const notifTitle = taskName ? `Task chat · ${taskName}` : "Task chat";
          let messageBody = "New message";
          const mtype = env.messageType || "text";
          if (mtype === "image") messageBody = "Photo";
          else if (mtype === "video") messageBody = "Video";
          else if (mtype === "audio") messageBody = "Audio";
          else if (mtype === "document") messageBody = env.documentFileName ? String(env.documentFileName).slice(0, 80) : "Document";
          else { const text = String(env.body || "").trim(); if (text) messageBody = text.length > 120 ? `${text.slice(0, 120)}…` : text; }
          showTaskChatBrowserNotification(notifTitle, `${senderName}: ${messageBody}`, settings, { taskId: String(payload.taskId) }).catch(() => {});
        }
      }
    }
  } catch (error) { console.error("handleTaskChatMessage:", error); }
};

export const handleTaskChatLogNotification = (payload) => {
  try {
    const state = store.getState();
    const settings = state.notifications?.settings || { browserNotificationsEnabled: true };
    if (!settings.browserNotificationsEnabled) return;
    // Sound follows the same notification toggle — no separate sound setting.
    try { playNotificationSound(); } catch (_) {}
    const taskId = payload?.taskId ? String(payload.taskId) : "";
    const actorName = String(payload?.actorName || "").trim();
    const taskName = resolveTaskDisplayNameForNotification(
      state.tasks,
      taskId,
      payload?.taskName
    );
    const summary = String(payload?.summary || "").trim();
    const firstLine = summary.split("\n").find((line) => line.trim()) || "";
    const fallbackTitle = actorName ? `Task log · ${actorName}` : "Task log";
    const title = buildTaskLogNotificationTitle(taskName, fallbackTitle);
    const body = buildTaskLogNotificationBody(actorName, firstLine);
    showTaskChatBrowserNotification(title, body, settings, { taskId: taskId || undefined }).catch(() => {});
  } catch (_) {}
};

export const handleTaskChatMessageStatusClient = (payload) => {
  if (!ensureDispatchReady()) { queueEvent("task_chat:message_status", payload, handleTaskChatMessageStatusClient); return; }
  try {
    const taskId = payload?.taskId; const readerId = payload?.readerId;
    if (!taskId || readerId == null) return;
    const dispatchFn = getDispatch();
    if (!dispatchFn) return;
    const state = store.getState();
    const viewerId = state.auth?.user?._id || state.auth?.user?.id || state.userDetails?.userDetails?._id || state.userDetails?.userDetails?.id || "";
    const readAt = payload?.readAt || new Date().toISOString();
    dispatchFn(taskChatReadReceiptBatch({ taskId, readerId: String(readerId), readAt, viewerId: String(viewerId) }));
  } catch (error) { console.error("handleTaskChatMessageStatusClient:", error); }
};

export const handleTaskUpdated = (payload) => {
  if (!ensureDispatchReady()) { queueEvent("task:updated", payload, handleTaskUpdated); return; }
  try {
    const dispatchFn = getDispatch();
    if (!dispatchFn || !payload?.task) return;
    const taskId = String(
      payload.taskId || payload.task._id || payload.task.id || ""
    ).trim();
    const taskLabel = resolveTaskDisplayName(payload.task);
    if (taskId && taskLabel) rememberTaskDisplayName(taskId, taskLabel);

    const state = store.getState();
    const viewerId = state.auth?.user?._id || state.auth?.user?.id || state.userDetails?.userDetails?._id || state.userDetails?.userDetails?.id || "";
    if (payload.changedBy && viewerId && String(payload.changedBy) === String(viewerId)) return;
    dispatchFn(applyTaskRealtimeUpdate(payload.task));
  } catch (error) { console.error("handleTaskUpdated:", error); }
};

export const handleTaskDeleted = (payload) => {
  if (!ensureDispatchReady()) { queueEvent("task:deleted", payload, handleTaskDeleted); return; }
  try {
    const dispatchFn = getDispatch();
    if (!dispatchFn || !payload?.taskId) return;
    const state = store.getState();
    const viewerId = state.auth?.user?._id || state.auth?.user?.id || state.userDetails?.userDetails?._id || state.userDetails?.userDetails?.id || "";
    if (payload.changedBy && viewerId && String(payload.changedBy) === String(viewerId)) return;
    dispatchFn(applyTaskRealtimeDelete(String(payload.taskId)));
  } catch (error) { console.error("handleTaskDeleted:", error); }
};
